import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { Plus, Loader2, RefreshCw, MessagesSquare, Rss, Users, Calendar } from 'lucide-react';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { gsap } from 'gsap';
import toast from 'react-hot-toast';
import { ADMIN_COLORS } from '@/constants/theme';
import { API_URL } from '@/network/http';
import { GlassCard } from '@/components/glass';
import {
  useFeed,
  useDeletePost,
  useTogglePin,
  useRecordView,
  useBreakingNews,
} from '../hooks';
import { PostCard } from '../components/PostCard';
import { CreatePostModal } from '../components/CreatePostModal';
import { CommentsSheet } from '../components/CommentsSheet';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';
import { ArticleViewerModal } from '../components/ArticleViewerModal';
import { UserProfileModal } from '../components/UserProfileModal';
import { BreakingNewsTicker } from '../components/BreakingNewsTicker';
import { StatusUpdateBox } from '../components/StatusUpdateBox';
import type { FeedPost } from '../types';

// ═══════════════════════════════════════════════════════════════════
// Feed ranking — tiered sort for the flat post list
//   Tier 0  Fresh user posts (last FRESH_WINDOW)
//   Tier 1  Pinned posts
//   Tier 2  Everything else — user posts get a virtual time boost
// Within each tier, newer posts come first.
// ═══════════════════════════════════════════════════════════════════
const FRESH_WINDOW_MS = 24 * 60 * 60 * 1000;  // 24 h
const USER_BOOST_MS   =  6 * 60 * 60 * 1000;  //  6 h virtual boost for user posts in tier 2

function rankFeed(posts: FeedPost[]): FeedPost[] {
  const now = Date.now();

  return posts.slice().sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();
    const aIsUser = a.source !== 'rss';
    const bIsUser = b.source !== 'rss';
    const aFresh = aIsUser && (now - aTime) < FRESH_WINDOW_MS;
    const bFresh = bIsUser && (now - bTime) < FRESH_WINDOW_MS;

    // Tier 0 — fresh user posts always on top
    if (aFresh !== bFresh) return aFresh ? -1 : 1;
    if (aFresh && bFresh) return bTime - aTime;

    // Tier 1 — pinned posts
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    if (a.isPinned && b.isPinned) return bTime - aTime;

    // Tier 2 — remaining: user posts get a virtual time boost so they
    // surface above RSS posts of a similar age
    const aScore = aTime + (aIsUser ? USER_BOOST_MS : 0);
    const bScore = bTime + (bIsUser ? USER_BOOST_MS : 0);
    return bScore - aScore;
  });
}

// ═══════════════════════════════════════════════════════════════════
// Local skeleton tokens (unique to this component)
// ═══════════════════════════════════════════════════════════════════
const FEED_COLORS = {
  skeleton: 'rgba(0,44,65,0.08)',
  skeletonDark: 'rgba(0,44,65,0.15)',
} as const;

// ═══════════════════════════════════════════════════════════════════
// Skeleton Loader Card
// ═══════════════════════════════════════════════════════════════════
function PostCardSkeleton() {
  return (
    <GlassCard padding="none" className="feed-post-flat">
      {/* Header: FB-style 16px padding, 40px avatar, 12px gap */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white/80 border-b" style={{ borderColor: '#CED0D4' }}>
        <div
          className="h-10 w-10 rounded-full animate-pulse flex-shrink-0"
          style={{ backgroundColor: FEED_COLORS.skeletonDark }}
        />
        <div className="flex-1 space-y-2">
          <div
            className="h-4 w-28 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeletonDark }}
          />
          <div
            className="h-3 w-16 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeleton }}
          />
        </div>
      </div>

      {/* Content lines — 16px horizontal padding, 8px vertical */}
      <div className="px-4 pt-2 pb-2 space-y-2">
        <div
          className="h-3.5 w-full rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeleton }}
        />
        <div
          className="h-3.5 w-4/5 rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeleton }}
        />
      </div>

      {/* Media: full-width rectangle */}
      <div
        className="w-full h-48 animate-pulse"
        style={{ backgroundColor: FEED_COLORS.skeleton }}
      />

      {/* Reaction summary: overlapping circles + text — 8px vertical */}
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-1.5">
          <div className="flex -space-x-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-5 h-5 rounded-full animate-pulse border-2 border-white"
                style={{ backgroundColor: FEED_COLORS.skeletonDark }}
              />
            ))}
          </div>
          <div
            className="h-3 w-6 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeleton }}
          />
        </div>
        <div
          className="h-3 w-16 rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeleton }}
        />
      </div>

      {/* Action bar: 2 centered buttons — 8px vertical padding */}
      <div className="flex items-center border-t" style={{ borderColor: '#CED0D4' }}>
        <div className="flex-1 flex items-center justify-center py-2">
          <div
            className="h-4 w-14 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeleton }}
          />
        </div>
        <div className="w-px h-5" style={{ backgroundColor: '#CED0D4' }} />
        <div className="flex-1 flex items-center justify-center py-2">
          <div
            className="h-4 w-18 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeleton }}
          />
        </div>
      </div>
    </GlassCard>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RSS Skeleton Loader Card — mirrors RSS PostCard structure
// ═══════════════════════════════════════════════════════════════════
function RssCardSkeleton() {
  return (
    <GlassCard padding="none" className="feed-post-flat">
      {/* Header: orange avatar (RSS is always orange) */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white/80 border-b" style={{ borderColor: '#CED0D4' }}>
        <div
          className="h-10 w-10 rounded-full flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #f97316, #fb923c)' }}
        />
        <div className="flex-1 space-y-2">
          <div
            className="h-4 w-24 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeletonDark }}
          />
          <div className="flex items-center gap-2">
            <div
              className="h-3 w-14 rounded animate-pulse"
              style={{ backgroundColor: FEED_COLORS.skeleton }}
            />
            <div
              className="h-3.5 w-10 rounded-full animate-pulse"
              style={{ backgroundColor: 'rgba(249,115,22,0.15)' }}
            />
          </div>
        </div>
      </div>

      {/* Title lines (bolder) + body lines */}
      <div className="px-4 pt-2 pb-1 space-y-1.5">
        <div
          className="h-4 w-full rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeletonDark }}
        />
        <div
          className="h-4 w-3/4 rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeletonDark }}
        />
      </div>
      <div className="px-4 pb-2 space-y-1.5">
        <div
          className="h-3.5 w-full rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeleton }}
        />
        <div
          className="h-3.5 w-5/6 rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeleton }}
        />
      </div>

      {/* "Read article" link placeholder */}
      <div className="px-4 pb-2">
        <div
          className="h-3 w-24 rounded animate-pulse"
          style={{ backgroundColor: 'rgba(249,115,22,0.18)' }}
        />
      </div>

      {/* Media: smaller than user posts */}
      <div
        className="w-full h-32 animate-pulse"
        style={{ backgroundColor: FEED_COLORS.skeleton }}
      />

      {/* Reaction summary */}
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-1.5">
          <div className="flex -space-x-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-5 h-5 rounded-full animate-pulse border-2 border-white"
                style={{ backgroundColor: FEED_COLORS.skeletonDark }}
              />
            ))}
          </div>
          <div
            className="h-3 w-6 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeleton }}
          />
        </div>
        <div
          className="h-3 w-16 rounded animate-pulse"
          style={{ backgroundColor: FEED_COLORS.skeleton }}
        />
      </div>

      {/* Action bar */}
      <div className="flex items-center border-t" style={{ borderColor: '#CED0D4' }}>
        <div className="flex-1 flex items-center justify-center py-2">
          <div
            className="h-4 w-14 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeleton }}
          />
        </div>
        <div className="w-px h-5" style={{ backgroundColor: '#CED0D4' }} />
        <div className="flex-1 flex items-center justify-center py-2">
          <div
            className="h-4 w-18 rounded animate-pulse"
            style={{ backgroundColor: FEED_COLORS.skeleton }}
          />
        </div>
      </div>
    </GlassCard>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FeedPage — main component
// ═══════════════════════════════════════════════════════════════════
export function FeedPage() {
  const { t } = useTranslation();

  // ─── Feed data ─────────────────────────────────────────────────
  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    isRefetching,
  } = useFeed();

  // Breaking news IDs for client-side filtering (belt-and-suspenders with backend)
  const { data: breakingItems } = useBreakingNews();
  const breakingIds = useMemo(
    () => new Set(breakingItems?.map((b) => b.id) ?? []),
    [breakingItems],
  );

  const posts = useMemo(
    () =>
      rankFeed(
        (data?.pages.flatMap((page) => page.feed) ?? [])
          .filter((post) => !breakingIds.has(post.id)),
      ),
    [data, breakingIds],
  );

  const isMobile = useMediaQuery(MD_BREAKPOINT);

  const rssPosts = useMemo(
    () => (isMobile ? [] : posts.filter((p) => p.source === 'rss')),
    [posts, isMobile],
  );

  const feedPosts = useMemo(
    () => (isMobile ? posts : posts.filter((p) => p.source !== 'rss')),
    [posts, isMobile],
  );

  // ─── Mutations ─────────────────────────────────────────────────
  const deletePost = useDeletePost();
  const togglePin = useTogglePin();
  const recordView = useRecordView();

  // ─── Modal/Sheet states ────────────────────────────────────────
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<FeedPost | undefined>(undefined);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [commentsPostId, setCommentsPostId] = useState<string | null>(null);
  const [articlePost, setArticlePost] = useState<FeedPost | null>(null);
  const [articleIframeUrl, setArticleIframeUrl] = useState<string | undefined>(undefined);
  const [articleSrcdoc, setArticleSrcdoc] = useState<string | undefined>(undefined);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);

  // ─── Pull-to-refresh ──────────────────────────────────────────
  const [pullY, setPullY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const touchStartY = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const container = scrollContainerRef.current;
    if (!container || container.scrollTop > 0) return;

    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY > 0) {
      setPullY(Math.min(deltaY * 0.4, 80));
      setIsPulling(true);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullY > 60 && !isRefetching) {
      refetch();
    }
    setPullY(0);
    setIsPulling(false);
  }, [pullY, isRefetching, refetch]);

  // ─── IntersectionObserver sentinel for infinite scroll ─────────
  const sentinelRef = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    rootMargin: '600px',
  });

  // ─── Record views when posts become visible ────────────────────
  const viewedPostsRef = useRef(new Set<string>());

  useEffect(() => {
    if (posts.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const postId = (entry.target as HTMLElement).dataset.postId;
            if (postId && !viewedPostsRef.current.has(postId)) {
              viewedPostsRef.current.add(postId);
              recordView.mutate(postId);
            }
          }
        }
      },
      { threshold: 0.5 },
    );

    const container = cardsContainerRef.current;
    if (!container) return;

    const cards = container.querySelectorAll('[data-post-id]');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [posts.length, recordView]);

  // ─── GSAP entrance animation ──────────────────────────────────
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const gsapCtxRef = useRef<gsap.Context | null>(null);
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (isLoading || posts.length === 0) return;

    const container = cardsContainerRef.current;
    if (!container) return;

    const allCards = container.querySelectorAll('[data-feed-card]');
    const prevCount = prevCountRef.current;
    prevCountRef.current = allCards.length;

    // Only animate newly added cards (skip already-visible ones)
    const newCards = Array.from(allCards).slice(prevCount);
    if (newCards.length === 0) return;

    // Initial load: use context for cleanup; subsequent: just animate new cards
    if (prevCount === 0) {
      gsapCtxRef.current?.revert();
      gsapCtxRef.current = gsap.context(() => {
        gsap.fromTo(
          newCards,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out', clearProps: 'all' },
        );
      }, container);
    } else {
      gsap.fromTo(
        newCards,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.35, stagger: 0.04, ease: 'power2.out', clearProps: 'all' },
      );
    }

    return () => {
      if (prevCount === 0) gsapCtxRef.current?.revert();
    };
  }, [isLoading, posts.length]);

  // ─── Handlers ──────────────────────────────────────────────────
  const handleEdit = useCallback((post: FeedPost) => {
    setEditingPost(post);
    setIsCreateModalOpen(true);
  }, []);

  const handleDeleteRequest = useCallback((id: string) => {
    setDeleteTargetId(id);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteTargetId) return;
    deletePost.mutate(deleteTargetId, {
      onSuccess: () => {
        toast.success(t('feed.postDeleted'));
        setDeleteTargetId(null);
      },
    });
  }, [deleteTargetId, deletePost, t]);

  const handleOpenComments = useCallback((post: FeedPost) => {
    setCommentsPostId(post.id);
  }, []);

  const handleReadArticle = useCallback((post: FeedPost) => {
    setArticlePost(post);
  }, []);

  const handleOpenNewPost = useCallback(() => {
    setEditingPost(undefined);
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setEditingPost(undefined);
  }, []);

  const handlePin = useCallback(
    (postId: string) => {
      togglePin.mutate(postId, {
        onSuccess: (result) => {
          toast.success(
            result.isPinned ? t('feed.post_pinned') : t('feed.post_unpinned'),
          );
        },
      });
    },
    [togglePin, t],
  );

  // Get comment count for the currently open comments sheet
  const commentsPost = useMemo(
    () => posts.find((p) => p.id === commentsPostId),
    [posts, commentsPostId],
  );

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  // Error state
  if (isError && !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <GlassCard padding="none" contentClassName="p-8">
          <MessagesSquare
            className="mx-auto mb-4 h-12 w-12"
            style={{ color: ADMIN_COLORS.navy + '30' }}
          />
          <p className="text-sm font-medium text-neutral-800 mb-4">
            {t('feed.error')}
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white transition-all active:scale-[0.98]"
            style={{
              backgroundColor: ADMIN_COLORS.primary,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadow,
            }}
          >
            <RefreshCw className="h-4 w-4" />
            {t('feed.retry')}
          </button>
        </GlassCard>
      </div>
    );
  }

  return (
    <>
      {/* Facebook-style scroll fade — bottom edge (fixed, mobile only) */}
      <div
        className="pointer-events-none fixed inset-x-0 z-10 md:hidden"
        style={{
          bottom: 0,
          height: 50,
          background: 'linear-gradient(to top, #F0F2F5, transparent)',
        }}
        aria-hidden="true"
      />

      {/* Content wrapper — re-add padding removed by HeroDockLayout's full-bleed */}
      <div className="px-4 sm:px-6 md:px-8">

      {/* Breaking news ticker — full width above both columns on desktop */}
      {!isMobile && <BreakingNewsTicker />}

      <div className="md:flex md:items-start md:gap-6 md:mt-0">

        {/* RSS Sidebar — desktop only */}
        {!isMobile && (isLoading || rssPosts.length > 0) && (
          <aside className="hidden md:block sticky top-6" style={{ flex: '0 2 420px', minWidth: 280 }}>
            <div className="flex items-center gap-2 px-3 py-2.5 mb-2">
              <Rss className="h-4 w-4 text-orange-500" />
              <h2 className="text-sm font-bold text-neutral-800">{t('feed.latestNews')}</h2>
            </div>
            <div className="space-y-3 max-h-[calc(100dvh-6rem)] overflow-y-auto hide-scrollbar pe-1">
              {isLoading ? (
                [1, 2, 3].map((i) => <RssCardSkeleton key={`rss-skel-${i}`} />)
              ) : (
                <>
                  {rssPosts.map((post) => (
                    <div key={post.id}>
                      <PostCard
                        post={post}
                        onComment={handleOpenComments}
                        onEdit={handleEdit}
                        onDelete={handleDeleteRequest}
                        onPin={handlePin}
                        onReadArticle={handleReadArticle}
                        onAuthorClick={setProfileUserId}
                      />
                    </div>
                  ))}
                  {isFetchingNextPage && (
                    [1, 2].map((i) => <RssCardSkeleton key={`rss-next-${i}`} />)
                  )}
                </>
              )}
            </div>
          </aside>
        )}

        {/* Feed column */}
        <div
          ref={scrollContainerRef}
          className="pb-32 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 md:mx-0 md:mt-0 md:max-w-[680px]"
          style={{ flex: '1 1 680px', minWidth: 380 }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >

          {/* Breaking news ticker — mobile only (desktop renders above both columns) */}
          {isMobile && <BreakingNewsTicker />}

          {/* Feed headline — desktop only, matching sidebar header style */}
          {!isMobile && (
            <div className="flex items-center gap-2 px-3 py-2.5 mb-2">
              <Users className="h-4 w-4 text-blue-500" />
              <h2 className="text-sm font-bold text-neutral-800">{t('feed.communityFeed')}</h2>
            </div>
          )}

          {/* Status update box (Facebook-style "What's on your mind?") */}
          <StatusUpdateBox onOpenNewPost={handleOpenNewPost} />

          {/* Pull-to-refresh indicator */}
          {isPulling && (
            <div
              className="flex items-center justify-center transition-all duration-200"
              style={{
                height: `${pullY}px`,
                overflow: 'hidden',
              }}
            >
              <RefreshCw
                className={`h-5 w-5 transition-transform ${
                  pullY > 60 ? 'text-primary-500' : 'text-slate-800/40'
                }`}
                style={{
                  transform: `rotate(${pullY * 3}deg)`,
                }}
              />
            </div>
          )}

          {/* Refetching banner */}
          {isRefetching && !isFetchingNextPage && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: ADMIN_COLORS.primary }} />
            </div>
          )}

          {/* Loading skeleton */}
          {isLoading ? (
            <div className="space-y-0.5 md:space-y-3 pt-1">
              {[1, 2, 3].map((i) => (
                <PostCardSkeleton key={i} />
              ))}
            </div>
          ) : feedPosts.length === 0 ? (
            /* Empty state */
            <div className="py-16 text-center">
              <GlassCard padding="none" contentClassName="p-10" className="mx-auto">
                <MessagesSquare
                  className="mx-auto mb-4 h-14 w-14"
                  style={{ color: ADMIN_COLORS.navy + '30' }}
                />
                <p className="text-base font-medium text-neutral-800 mb-1">
                  {t('feed.empty')}
                </p>
                <p className="text-sm text-neutral-500 mb-6">
                  {t('feed.emptySubtitle')}
                </p>
                <button
                  onClick={handleOpenNewPost}
                  className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white transition-all active:scale-[0.98]"
                  style={{
                    backgroundColor: ADMIN_COLORS.primary,
                    border: `1px solid ${ADMIN_COLORS.border}`,
                    boxShadow: ADMIN_COLORS.shadow,
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {t('feed.newPost')}
                </button>
              </GlassCard>
            </div>
          ) : (
            /* Feed cards */
            <div ref={cardsContainerRef} className="space-y-0.5 md:space-y-3 pt-1">
              {feedPosts.map((post) => (
                <div key={post.id} data-feed-card data-post-id={post.id}>
                  <PostCard
                    post={post}
                    onComment={handleOpenComments}
                    onEdit={handleEdit}
                    onDelete={handleDeleteRequest}
                    onPin={handlePin}
                    onReadArticle={handleReadArticle}
                    onAuthorClick={setProfileUserId}
                  />
                </div>
              ))}

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="h-1" />

              {/* Loading more skeletons */}
              {isFetchingNextPage && (
                <div className="space-y-1">
                  {[1, 2].map((i) => (
                    <PostCardSkeleton key={`next-${i}`} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Calendar sidebar — desktop only */}
        {!isMobile && (
          <aside className="hidden md:block sticky top-6" style={{ flex: '0 3 320px', minWidth: 200 }}>
            <div className="flex items-center gap-2 px-3 py-2.5 mb-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              <h2 className="text-sm font-bold text-neutral-800">{t('feed.calendar')}</h2>
            </div>
            <div className="feed-calendar bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-3">
              <FullCalendar
                plugins={[dayGridPlugin]}
                initialView="dayGridMonth"
                locale="en"
                timeZone="Asia/Jerusalem"
                headerToolbar={{
                  left: 'prev',
                  center: 'title',
                  right: 'next',
                }}
                events={'https://www.hebcal.com/hebcal?cfg=fc&v=1&i=on&maj=on&lg=en'}
                eventColor={ADMIN_COLORS.primary}
                eventDisplay="dot"
                height="auto"
                dayHeaderFormat={{ weekday: 'narrow' }}
                eventClick={(info) => {
                  info.jsEvent.preventDefault();
                  const originalUrl = info.event.url || '';
                  const title = info.event.title || '';
                  const description = (info.event.extendedProps?.description as string) || '';

                  // Set post data immediately so modal opens with loading state
                  setArticlePost({
                    id: `holiday-${originalUrl}`,
                    content: `${title}\n\n${description}`,
                    mediaUrl: null,
                    mediaType: null,
                    mediaMeta: null,
                    bgGradient: null,
                    isPinned: false,
                    viewCount: 0,
                    source: 'rss',
                    sourceUrl: originalUrl,
                    sourceName: title,
                    sourceIconUrl: null,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    author: { id: '', firstName: null, lastName: null, avatarUrl: null, role: 'THERAPIST' },
                    reactionCounts: {} as FeedPost['reactionCounts'],
                    userReactions: [],
                    commentCount: 0,
                  });

                  // Fetch HTML via proxy, rewrite URLs client-side, use srcdoc
                  const proxyUrl = `${API_URL}/proxy/iframe?url=${encodeURIComponent(originalUrl)}`;
                  fetch(proxyUrl)
                    .then((res) => res.text())
                    .then((html) => {
                      // Rewrite relative URLs to absolute
                      try {
                        const base = new URL(originalUrl);
                        const origin = `${base.protocol}//${base.host}`;
                        html = html.replace(/(href|src|action)="\/(?!\/)/g, `$1="${origin}/`);
                        html = html.replace(/(href|src|action)='\/(?!\/)/g, `$1='${origin}/`);
                        // Also handle url() in inline styles
                        html = html.replace(/url\(\/(?!\/)/g, `url(${origin}/`);
                      } catch { /* ignore parse errors */ }
                      setArticleSrcdoc(html);
                    })
                    .catch(() => {
                      // Fallback: just open in new tab
                      window.open(originalUrl, '_blank');
                    });
                }}
              />
            </div>
          </aside>
        )}
      </div>
      </div>

      {/* Create/Edit Post Modal */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        editPost={editingPost}
      />

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={confirmDelete}
        isPending={deletePost.isPending}
      />

      {/* Comments Sheet */}
      {commentsPostId && (
        <CommentsSheet
          postId={commentsPostId}
          isOpen={!!commentsPostId}
          onClose={() => setCommentsPostId(null)}
          commentCount={commentsPost?.commentCount ?? 0}
        />
      )}

      {/* Article Viewer Modal */}
      <ArticleViewerModal
        post={articlePost}
        isOpen={!!articlePost}
        onClose={() => { setArticlePost(null); setArticleIframeUrl(undefined); setArticleSrcdoc(undefined); }}
        iframeUrl={articleIframeUrl}
        srcdocHtml={articleSrcdoc}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        userId={profileUserId}
        isOpen={!!profileUserId}
        onClose={() => setProfileUserId(null)}
      />
    </>
  );
}
